// ─── Il contratto dell'accesso ───────────────────────────────────────────────
//
// Uno schema solo per rotta: valida il corpo lato server, tipizza il client
// React. Nessun tipo scritto due volte.
import { z } from 'zod';
import { permessoSchema, ruoloSchema } from './permessi.js';
/**
 * La lunghezza minima di una password NUOVA.
 *
 * ⚖️ Vale solo per le password che si impostano da oggi: quelle ereditate da
 * GoTrue (§ 05.2) entrano con l'hash che hanno, e nessuno deve rifarle. Una
 * regola applicata all'indietro chiuderebbe fuori cinque persone la mattina
 * del passaggio.
 */
export const LUNGHEZZA_MINIMA_PASSWORD = 10;
export const passwordNuovaSchema = z
    .string()
    .min(LUNGHEZZA_MINIMA_PASSWORD, `La password deve essere lunga almeno ${LUNGHEZZA_MINIMA_PASSWORD} caratteri`)
    .max(200, 'La password è troppo lunga');
export const emailSchema = z
    .string()
    .trim()
    .toLowerCase()
    .email("L'indirizzo email non è scritto bene")
    .max(200);
export const loginInputSchema = z.object({
    email: emailSchema,
    // In ingresso NON si applica la lunghezza minima: una password vecchia più
    // corta deve poter entrare. La regola sta in scrittura, non in lettura.
    password: z.string().min(1, 'Serve la password').max(200),
});
/** Chi sono, e cosa posso: è la risposta di `/me` e la coda di `/login`. */
export const profiloSchema = z.object({
    id: z.uuid(),
    email: z.string(),
    fullName: z.string().nullable(),
    role: ruoloSchema,
    /**
     * I permessi EFFETTIVI, non le righe della tabella: per `owner` e `admin`
     * sono tutti. Il frontend nasconde ciò che l'utente non può usare, e per
     * farlo non deve riscrivere la regola di `puo()` — la riceve già applicata.
     */
    permissions: z.array(permessoSchema),
    /**
     * 🎾 La scheda maestro agganciata a questo accesso, se c'è ed è attiva.
     *
     * ⭐ Sta nel profilo e non dietro una chiamata a parte perché `App.tsx` deve
     * scegliere **quale guscio montare** prima di qualunque altra cosa: un maestro
     * non vede una versione ridotta del gestionale, vede un'altra applicazione.
     * Scoprirlo dopo vorrebbe dire far lampeggiare la schermata sbagliata.
     * ⚠️ `null` per quasi tutti, ed è lo stato normale: un operatore di
     * segreteria non è un maestro.
     */
    maestro: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
    mustChangePassword: z.boolean(),
});
/**
 * Il gettone di accesso e chi lo possiede.
 *
 * ⛔ Il gettone di RINNOVO non è qui: viaggia in un cookie `HttpOnly`, che il
 * JavaScript della pagina non può leggere. Metterlo nel corpo lo consegnerebbe
 * a qualunque script finisse nella pagina.
 */
export const sessioneApertaSchema = z.object({
    accessToken: z.string(),
    expiresInSeconds: z.number().int().positive(),
    user: profiloSchema,
});
export const registerInputSchema = z.object({
    email: emailSchema,
    password: passwordNuovaSchema,
});
export const cambioPasswordInputSchema = z.object({
    currentPassword: z.string().min(1, 'Serve la password attuale').max(200),
    newPassword: passwordNuovaSchema,
});
export const esitoSempliceSchema = z.object({ ok: z.literal(true) });
// ─── La riapertura dell'accesso, per chi la password se la dimentica ─────────
//
// ⚖️ NON è «reset password per tutti» — che il § README.6 ha scartato — ed è
// una distinzione di sostanza, non di parole: là si trattava di far rifare la
// password a **tutti** il giorno del passaggio, buttando via gli hash ereditati.
// Qui si riapre **un** accesso, a chi lo chiede, lasciando intatti gli altri.
//
// 🚨 E SONO DUE STRADE, di proposito, perché una sola non basta mai:
//   ① l'operatore chiede da sé e gli arriva un'email — funziona solo se la
//     posta è configurata, e quando non lo è il gestionale **lo dichiara**
//     invece di far aspettare un messaggio che non partirà;
//   ② un proprietario riapre l'accesso dalle Impostazioni — funziona sempre,
//     senza nessuna configurazione, ed è la strada del giorno del travaso,
//     quando gli operatori arrivano tutti senza password (§ 05.2).
// 📌 Una via di rientro che dipende da una casella di configurazione non è una
//    via di rientro: è una promessa.
/**
 * Quanto vive il gettone che arriva per email.
 *
 * ⚖️ Corto apposta: è l'unica cosa che, per quella mezz'ora, vale quanto la
 * password di quella persona. Un'ora di comodità in più è un'ora in più in cui
 * una casella di posta letta da qualcun altro apre il gestionale.
 */
export const DURATA_RIAPERTURA_MINUTI = 30;
export const richiestaRiaperturaSchema = z.object({ email: emailSchema });
export const riaperturaInputSchema = z.object({
    // Non è un identificativo: è un segreto di 32 byte in base64url. La misura
    // minima serve solo a non far arrivare al database una stringa vuota.
    token: z.string().min(20, 'Il collegamento non è valido').max(400),
    newPassword: passwordNuovaSchema,
});
/**
 * Cosa questo gestionale sa fare, prima di essere entrati.
 *
 * ⛔ Non dice niente su NESSUN account — è un fatto della configurazione, non
 * delle persone: sapere che la posta è spenta non aiuta chi tira a indovinare.
 * ⭐ Serve alla schermata di accesso per non offrire «ho dimenticato la
 * password» quando quel bottone non potrebbe mandare niente a nessuno.
 */
export const capacitaAccessoSchema = z.object({
    /** `false` ⇒ la riapertura la fa un proprietario dalle Impostazioni. */
    passwordResetByEmail: z.boolean(),
});
//# sourceMappingURL=auth.js.map